import type { Job } from '../firestore/types';
import type { JobMatchScore, ReadinessBreakdown, ReadinessProfile } from '../../types/readiness';
import { getYearsExperienceForReadiness } from '../experienceRange';
import { certificationJobContext, getJobCertRequirements, getCertificationMatch } from '../certificationMatching';
import { countingCertificationEntries, scoringClock, scoringToday } from './certUtils';
import {
  certificationMatchKey,
  certificationSatisfies,
  certificationSatisfiesForJob,
  compactPrivilegeApplies,
  COMPACT_PRIVILEGE_LABEL,
  STATE_EMS_CERTIFICATION,
  displayCertificationName,
  formatCertificationName,
  type CertificationJobContext,
  type HeldCertification,
} from '../constants/certifications';

/** The candidate's held certification (display name) that satisfies `required`, exact match preferred. */
export function heldCertificationFor(
  heldNames: readonly string[],
  required: string,
  entries?: readonly HeldCertification[] | null,
  ctx?: CertificationJobContext | null,
): string | null {
  const reqKey = certificationMatchKey(required);
  if (!reqKey) return null;
  // A required State EMS Certification met only through the EMS Compact (another Compact state's license)
  // is named as such; one from the job's own state keeps today's plain name.
  if (reqKey === certificationMatchKey(STATE_EMS_CERTIFICATION) && entries?.length) {
    const meetsIt = entries.filter((e) => certificationSatisfiesForJob(e.name, e.state, required, ctx, e.level));
    if (meetsIt.length && meetsIt.every((e) => compactPrivilegeApplies(e.state, ctx))) {
      return `${formatCertificationName(meetsIt[0].name, meetsIt[0].state, meetsIt[0].level)}, ${COMPACT_PRIVILEGE_LABEL}`;
    }
  }
  const exact = heldNames.find((c) => certificationMatchKey(c) === reqKey);
  if (exact) return displayCertificationName(exact);
  const higher = heldNames.find((c) => certificationSatisfies(c, required));
  if (higher) return displayCertificationName(higher);
  // State-aware: a Florida State EMS Certification at the right level meets an NREMT-level requirement.
  const viaState = (entries ?? []).find((e) => certificationSatisfiesForJob(e.name, e.state, required, ctx, e.level));
  if (!viaState) return null;
  const name = formatCertificationName(viaState.name, viaState.state, viaState.level);
  // Another Compact state's license: say so, in the agreed words.
  return compactPrivilegeApplies(viaState.state, ctx) && certificationMatchKey(required) === certificationMatchKey(viaState.name) ? `${name}, ${COMPACT_PRIVILEGE_LABEL}` : name;
}

function sameCertification(a: string, b: string): boolean {
  const ka = certificationMatchKey(a);
  return Boolean(ka) && ka === certificationMatchKey(b);
}

/**
 * Ceiling for a candidate missing a certification the employer marked REQUIRED. Low enough that the
 * match reads as "not eligible" rather than "decent" — an ineligible candidate must never outrank an
 * eligible one, whatever the rest of their profile looks like.
 */
export const MISSING_REQUIRED_CERT_MAX_SCORE = 35;

/**
 * Is this EMT open to relocating? Canonical home is professional.jobPreferences.openToRelocation
 * (set in signup step 3 and editable in the profile's Job Preferences section). The two legacy
 * `preferences.relocate === 'yes'` shapes are still read so accounts created before the field
 * existed keep whatever they had.
 */
export function isEmtOpenToRelocation(profile: ReadinessProfile): boolean {
  if (profile.professional?.jobPreferences?.openToRelocation === true) return true;
  if (profile.professional?.preferences?.relocate === 'yes') return true;
  return (profile as { preferences?: { relocate?: string } }).preferences?.relocate === 'yes';
}

const ADJACENT_STATES: Record<string, string[]> = {
  UT: ['AZ', 'CO', 'ID', 'NV', 'WY'],
  AZ: ['CA', 'CO', 'NM', 'NV', 'UT'],
  CO: ['KS', 'NE', 'NM', 'OK', 'UT', 'WY'],
  CA: ['AZ', 'NV', 'OR'],
  TX: ['AR', 'LA', 'NM', 'OK'],
  NY: ['CT', 'MA', 'NJ', 'PA', 'VT'],
};

function parseStateFromLocation(location: unknown): string {
  const text = typeof location === 'string'
    ? location
    : location && typeof location === 'object'
      ? String((location as { name?: string }).name || '')
      : '';
  const parts = text.split(',').map((s) => s.trim());
  const last = parts[parts.length - 1] || '';
  const code = last.length === 2 ? last.toUpperCase() : '';
  return code;
}

import { normalizeJobMatchFields } from '../jobs/jobFormModel';
import { dispatchJobFloor, driverJobFloor, isDispatchJob, isDriverJob } from '../jobs/driverRoles';
import { distanceScoreForMiles, jobMapPoint, nearestEmtPlace, travelDistanceMiles, type MilesScore } from './distanceScore';

function normalizeSkillList(job: Job): string[] {
  const { requiredSkills } = normalizeJobMatchFields(job as unknown as Record<string, unknown>);
  if (requiredSkills.length) return requiredSkills;
  return job.preferredSkills ?? [];
}

function normalizeRequiredYears(job: Job): number {
  return normalizeJobMatchFields(job as unknown as Record<string, unknown>).requiredYears;
}

/**
 * Options that change how a job is matched. `driverRoles` is the `driver_roles` feature flag: with
 * it on, a job whose roleCategory is 'driver' is matched on driver requirements only (see
 * src/lib/jobs/driverRoles.ts). Omitted or off: today's match, exactly.
 */
export type JobMatchOptions = {
  driverRoles?: boolean;
  /** "Now" for the counting rule (expiry). Read on the EMT's calendar (certUtils scoringClock). */
  now?: Date;
};

export function calculateJobMatch(
  profile: ReadinessProfile,
  job: Job,
  readiness: ReadinessBreakdown,
  opts: JobMatchOptions = {},
): JobMatchScore {
  const jobId = job.id || '';
  const warnings: string[] = [];
  // THE COUNTING RULE (certUtils certCounts, 2026-10-13): only credentials whose document passed review
  // and that have not expired on the EMT's calendar count, for required AND preferred items. A required
  // credential that is pending, rejected, never uploaded or expired is not met: the score is capped and
  // the apply gate (documentReviewGate) blocks it. Before 2026-10-13 any typed credential counted here.
  const now = scoringClock(profile, opts.now ?? new Date());
  const emtCertEntries = countingCertificationEntries(profile, now);
  const emtCertNames = emtCertEntries.map((e) => e.name);
  const jobReq = getJobCertRequirements(job as Parameters<typeof getJobCertRequirements>[0]);
  const required = jobReq.required ?? [];
  const preferred = jobReq.preferred ?? [];

  // Job context (its state, whether NREMT is required) and each held credential's state and level, so
  // the score agrees with the apply gate (state-aware NREMT rule, State EMS Certification per state).
  const certJobContext = certificationJobContext(job);
  const certMatch = getCertificationMatch({
    emtCertifications: emtCertNames,
    emtCertificationEntries: emtCertEntries,
    jobContext: certJobContext,
    requiredCertifications: required,
    preferredCertifications: preferred,
  });
  const missingRequired = certMatch.missingRequiredCertifications;
  // Which HELD certification satisfies each requirement. A Paramedic satisfies an AEMT requirement
  // (certifications.ts ladder), but the credit line must name what the candidate actually holds:
  // "✓ Certifications: AEMT" for someone who never held AEMT was a label-provenance bug (2026-09-24).
  const satisfied = required
    .map((r) => ({ required: displayCertificationName(r), satisfiedBy: heldCertificationFor(emtCertNames, r, emtCertEntries, certJobContext) }))
    .filter((s): s is { required: string; satisfiedBy: string } => Boolean(s.satisfiedBy));
  const matchedRequired = satisfied.map((s) => (sameCertification(s.required, s.satisfiedBy) ? s.required : `${s.satisfiedBy} (meets ${s.required})`));
  const matchedPreferred = certMatch.matchedPreferredCertifications;

  const hardGateFailed = missingRequired.length > 0;
  // Required credentials gate (0 when one is missing). Preferred credentials only RAISE the score:
  // with preferred listed, holding all required gives 80 plus up to 20 for the share of preferred
  // held, so a candidate with the preferred credentials ranks above one without (2026-10-08; the old
  // "+5 each, capped at 100" never moved anyone because 100 was already the cap).
  // Both sides are counted by match key: a job listing "EMT-B" and "NREMT-B" as preferred lists one
  // credential, and holding it must count once (before 2026-10-09 the matched side was not de-duplicated,
  // so EMT-B alone earned the full +20 on [EMT-B, NREMT-B, ACLS]).
  const preferredCount = new Set(preferred.map((p) => certificationMatchKey(p)).filter(Boolean)).size;
  const matchedPreferredCount = new Set(matchedPreferred.map((p) => certificationMatchKey(p)).filter(Boolean)).size;
  let certScore = missingRequired.length === 0 ? 100 : 0;
  if (missingRequired.length === 0 && preferredCount > 0) {
    certScore = 80 + Math.round((20 * Math.min(matchedPreferredCount, preferredCount)) / preferredCount);
  }
  if (hardGateFailed) {
    warnings.push(`Missing required: ${missingRequired.join(', ')}`);
  }

  const emtState = (profile.address?.state || '').trim().toUpperCase();
  const jobState = parseStateFromLocation(job.location);
  const jobAcceptsRelocation = normalizeJobMatchFields(job as unknown as Record<string, unknown>).openToRelocation;
  const emtOpenToRelocation = isEmtOpenToRelocation(profile);

  // Location is only scorable when we know both ends. With a state missing on either side this used
  // to fall through to 20/100 "Different region" — inventing a penalty out of absent data, and then
  // showing it to the candidate as a reason. Unknown now means "not applicable" and is left out of
  // the weighted average entirely.
  const locationKnown = Boolean(emtState && jobState);
  let locationScore = 20;
  let locationExplanation = locationKnown ? 'Different region' : 'Location not specified';
  let locationPositive = false;
  if (locationKnown && emtState === jobState) {
    locationScore = 100;
    locationExplanation = `Same state (${emtState})`;
    locationPositive = true;
  } else if (locationKnown && ADJACENT_STATES[emtState]?.includes(jobState)) {
    locationScore = 70;
    locationExplanation = `Adjacent state (${jobState})`;
    locationPositive = true;
  } else if (jobAcceptsRelocation && emtOpenToRelocation) {
    locationScore = 70;
    locationExplanation = locationKnown
      ? `Different state (${jobState}): you're open to relocation and this job accepts it`
      : 'Open to relocation';
    locationPositive = true;
  } else if (locationKnown) {
    warnings.push(`Location: job is in ${jobState}, you're in ${emtState}`);
  }

  // By miles (2026-10-14, founder decision): when BOTH the EMT's address and the job have a map point,
  // distance is scored by straight-line miles (distanceScore.ts) instead of by state. No map point on
  // either side: the state rule above, exactly as before.
  // 2026-10-15 (B4): measured from the NEAREST of home and the EMT's extra cities (up to 5, each with
  // a map point); the travel distance applies around each of them.
  const jobPoint = jobMapPoint(job);
  const nearest = jobPoint ? nearestEmtPlace(profile, jobPoint) : null;
  let milesFacts: MilesScore | null = null;
  if (nearest) {
    milesFacts = distanceScoreForMiles(nearest.miles, travelDistanceMiles(profile), jobAcceptsRelocation && emtOpenToRelocation);
    locationScore = milesFacts.score;
    locationPositive = milesFacts.score >= 70;
    const m = Math.round(milesFacts.miles);
    const from = nearest.place.label ? ` from ${nearest.place.label}` : '';
    locationExplanation =
      milesFacts.reason === 'travel' ? `About ${m} miles${from}, within your travel distance`
        : milesFacts.reason === 'free' ? `About ${m} miles${from || ' away'}`
        : milesFacts.reason === 'relocation' ? `About ${m} miles${from || ' away'}: you're open to relocation and this job accepts it`
        : `About ${m} miles${from || ' away'}`;
    // Drop the state warning; say the miles instead when they cost points.
    for (let i = warnings.length - 1; i >= 0; i--) if (warnings[i].startsWith('Location: ')) warnings.splice(i, 1);
    if (milesFacts.score < 70) warnings.push(`Location: about ${m} miles away`);
  }

  const emtYears = getYearsExperienceForReadiness(profile as Parameters<typeof getYearsExperienceForReadiness>[0], scoringToday(now));
  const requiredYears = normalizeRequiredYears(job);
  let expScore = 100;
  if (requiredYears > 0) {
    // By how far short the EMT is. Years come with one decimal (2.5), so the old exact tests
    // ("=== required - 1") let 2.5 of 3 years score 50 while 2.0 scored 80 (2026-10-13 logic audit).
    const shortBy = requiredYears - emtYears;
    if (shortBy <= 0) expScore = 100;
    else if (shortBy <= 1) expScore = 80;
    else if (shortBy <= 2) expScore = 60;
    else expScore = Math.max(20, Math.round((emtYears / requiredYears) * 60));
    if (emtYears < requiredYears) {
      warnings.push(`Experience: ${emtYears} yrs (job requires ${requiredYears})`);
    }
  }

  const requiredSkills = normalizeSkillList(job);
  const rawSkills = profile.professional?.skills;
  let emtSkills: string[] = [];
  if (Array.isArray(rawSkills)) emtSkills = rawSkills;
  else if (rawSkills && typeof rawSkills === 'object') {
    emtSkills = Object.keys(rawSkills).filter((k) => rawSkills[k] === true);
  }
  const matchedSkills = requiredSkills.filter((s) =>
    emtSkills.some((e) => e.toLowerCase() === s.toLowerCase() || e.toLowerCase().includes(s.toLowerCase()))
  );
  const missingSkills = requiredSkills.filter((s) => !matchedSkills.includes(s));
  const skillsScore = requiredSkills.length
    ? Math.round((matchedSkills.length / requiredSkills.length) * 100)
    : 100;

  // A driver job (flag `driver_roles` on, job.roleCategory 'driver'): the EMS readiness score, whose
  // Credentials and Documentation parts are NREMT, a state EMS license and EMS skills, does not apply.
  // Its place is taken by what a driver job asks for: the driver documents the job lists, experience
  // and profile basics. Certifications above are already only the job's OWN requirements, so an EMS
  // certification counts only when the employer listed one. EMS jobs are unchanged.
  const driverJob = isDriverJob(job, opts.driverRoles === true);
  const driverFloor = driverJob ? driverJobFloor(profile, job, readiness.pillars, now) : null;
  // A dispatcher job: same idea, on dispatch background instead of driver documents.
  const dispatchFloor = isDispatchJob(job, opts.driverRoles === true) ? dispatchJobFloor(profile, readiness.pillars) : null;
  const readinessFloorScore = dispatchFloor ? dispatchFloor.score : driverFloor ? driverFloor.score : readiness.total;

  // Only pillars the job actually states a requirement for are averaged. A pillar the job is silent
  // about used to default to a full 100 and still carry its full weight, so a job that listed no
  // certifications, no required years and no required skills scored 40 + 20 + 10 = 70 of its 100
  // points for stating nothing. Re-weighting across the applicable pillars keeps every score an
  // honest answer to "how well does this candidate meet what this employer actually asked for".
  const certsApplicable = required.length > 0 || preferred.length > 0;
  const expApplicable = requiredYears > 0;
  const skillsApplicable = requiredSkills.length > 0;

  const locationApplicable = Boolean(milesFacts) || locationKnown || (jobAcceptsRelocation && emtOpenToRelocation);
  const contributions: Array<{ score: number; weight: number; applicable: boolean }> = [
    { score: certScore, weight: 40, applicable: certsApplicable },
    { score: locationScore, weight: 25, applicable: locationApplicable },
    { score: expScore, weight: 20, applicable: expApplicable },
    { score: skillsScore, weight: 10, applicable: skillsApplicable },
  ];
  // Whatever the job didn't ask for falls to the candidate's own readiness rather than being
  // treated as a free pass. Without this, a job that specifies nothing at all would score every
  // candidate on location alone — a completely empty profile in the right state would read as a
  // strong match.
  const slack = contributions.filter((c) => !c.applicable).reduce((s, c) => s + c.weight, 0);
  const readinessWeight = 5 + slack;
  const active = [...contributions.filter((c) => c.applicable), { score: readinessFloorScore, weight: readinessWeight, applicable: true }];
  const weightSum = active.reduce((s, c) => s + c.weight, 0);
  let total = weightSum > 0
    ? Math.round(active.reduce((s, c) => s + c.score * c.weight, 0) / weightSum)
    : 0;

  // Missing a certification the employer marked REQUIRED is disqualifying, not a deduction. The cap
  // used to be 50, which still reads as a middling-but-plausible match in the UI.
  if (hardGateFailed) total = Math.min(total, MISSING_REQUIRED_CERT_MAX_SCORE);

  return {
    jobId,
    total: Math.min(100, Math.max(0, total)),
    hardGateFailed,
    warnings,
    pillars: {
      certifications: {
        score: certScore,
        weight: 40,
        applicable: certsApplicable,
        positive: certsApplicable && !hardGateFailed,
        missing: missingRequired,
        matched: [...matchedRequired, ...matchedPreferred],
        satisfiedBy: satisfied,
      },
      location: {
        score: locationScore,
        weight: 25,
        applicable: locationApplicable,
        positive: locationPositive,
        explanation: locationExplanation,
        // The facts behind the location points, so the "Why?" breakdown can name them
        // (src/lib/readiness/jobScoreBreakdown.ts). Nothing here changes the score.
        emtState: emtState || null,
        jobState: jobState || null,
        adjacent: Boolean(locationKnown && emtState !== jobState && ADJACENT_STATES[emtState]?.includes(jobState)),
        jobAcceptsRelocation,
        emtOpenToRelocation,
        // By miles when both map points exist (distanceScore.ts); absent = scored by state.
        ...(milesFacts ? { scoredBy: 'miles' as const, miles: Math.round(milesFacts.miles * 10) / 10, milesReason: milesFacts.reason, travelMiles: milesFacts.travelMiles, fromCity: nearest?.place.label ?? null } : { scoredBy: 'state' as const }),
      },
      experience: {
        score: expScore,
        weight: 20,
        applicable: expApplicable,
        positive: expApplicable && emtYears >= requiredYears,
        emtYears,
        requiredYears,
      },
      skills: {
        score: skillsScore,
        weight: 10,
        applicable: skillsApplicable,
        positive: skillsApplicable && missingSkills.length === 0,
        matched: matchedSkills,
        missing: missingSkills,
      },
      readinessFloor: {
        score: readinessFloorScore,
        weight: 5,
        /** Base 5 plus the weight of every pillar this job didn't specify. */
        effectiveWeight: readinessWeight,
        applicable: true,
        positive: readinessFloorScore >= 50,
        ...(dispatchFloor ? { basis: 'dispatch' as const, dispatchBackground: dispatchFloor.dispatchBackground } : {}),
        ...(driverFloor
          ? { basis: 'driver' as const, driverItemsRequired: driverFloor.itemsRequired, driverItemsOnFile: driverFloor.itemsOnFile }
          : {}),
      },
    },
  };
}
